/**
 * Glue layer for single-player HighLow. Mirrors the imperative side-effect
 * orchestration in
 *   packages/amghighlow_v2/lib/presentation/screens/game_screen.dart
 *   packages/amghighlow_v2/lib/presentation/bloc/game_bloc.dart
 *
 * Cycle: 3 rounds × 2 cards = 6 card slots. After round 3 we shuffle and
 * restart from round 1.
 *
 *   Round 1: start card → CardFace1, result card → CardFace2
 *   Round 2: start card → CardFace3, result card → CardFace4
 *   Round 3: start card → CardFace5, result card → CardFace6 (also triggerPlayCard6)
 *
 * Rive event flow:
 *   placeBetClick { betID }     → placeBet + auto-confirm (no separate Play step)
 *   evtClickChips { chipsAmt }  → selectChip
 *   evtClearClick               → clearBets
 *   UIButtonClick startBetEnd   → triggerGirlAnim(1)
 *   UIButtonClick noMoreBet_End → PlayResultSequence (1000/3000/7000ms)
 *
 * REST flow:
 *   login → openGame → checkBalance → getInfo
 *   placeBet → response includes Result (CSV "first,second,winItem") and
 *              GameInitialInfo (CSV with next round's start card)
 */
import type { BetType } from '../types/game';
import { betTypeFromClickId, BET_ITEM_ID } from '../types/game';
import {
  DEFAULT_AGENT_ID,
  DEFAULT_GAME_CONFIG,
  DEFAULT_PASSWORD,
  GAME_KEY_SINGLE,
  ROUNDS_PER_CYCLE,
} from '../config';
import type { Api } from '../net/api';
import { ApiError } from '../net/api';
import type { GameState, GameStateSnapshot } from './gameState';
import type { RiveLayer } from '../rive/riveLayer';
import type { ChipLayer } from '../pixi/chipLayer';
import type { Hud } from '../ui/hud';
import type { TableControls } from '../ui/controls';
import type { UrlParams } from './urlParams';
import { parseNextFirstCard } from './resultParser';
import { GameLogManager } from './gameLogManager';
import { debugLog, error, warn } from '../util/logger';

interface OrchestratorDeps {
  state: GameState;
  api: Api;
  rive: RiveLayer;
  chips: ChipLayer;
  hud: Hud;
  controls: TableControls;
  params: UrlParams;
  onInteractive?: () => void;
}

type CardSlot = 1 | 2 | 3 | 4 | 5 | 6;

export class GameOrchestrator {
  private readonly d: OrchestratorDeps;
  private gid: string = '';

  /** Round-level guard flags. Reset at the start of each round / cycle. */
  private didStartReveal = false;
  private didShowResult = false;
  private isPlayResultRunning = false;
  // Balance settlement is deferred to the win/lose reveal (t=3000 in
  // playResultSequence) so the displayed (available) balance stays
  // "bet deducted" through the card reveal and only reflects winnings once
  // the result is shown.
  private pendingSettleBalance: number | null = null;
  private didSettle = false;
  private sequenceTimers: number[] = [];

  constructor(deps: OrchestratorDeps) {
    this.d = deps;
  }

  async start(): Promise<void> {
    this.d.state.on((next, prev) => this.onStateChange(next, prev));
    this.bindRiveEvents();
    this.d.controls.bindChips(this.d.state.config);
    this.d.controls.setSelectedChip(this.d.state.selectedChipIndex);
    this.d.controls.setControlsEnabled(false, false);

    this.d.hud.setUserName(this.d.params.userName);
    this.d.state.setPhase('initializing');
    this.d.hud.setStatus('Connecting…');

    try {
      await this.signIn();
      const firstCard = await this.loadConfigAndBalance();
      this.d.onInteractive?.();

      this.startCycle(firstCard);
    } catch (e) {
      error('[Orchestrator] start failed:', e);
      this.d.state.setError(extractMessage(e));
      this.d.hud.setStatus('ERROR: ' + extractMessage(e));
    }
  }

  // =================================================================
  // Bootstrap
  // =================================================================

  private async signIn(): Promise<void> {
    const { params } = this.d;
    const agentId = params.agentId || DEFAULT_AGENT_ID;

    let bearerToken: string;
    let gsid: string;

    if (params.token && params.gsid) {
      debugLog('[Orchestrator] Using token+gsid from URL params (no login)');
      bearerToken = ensureUrlEncoded(params.token);
      gsid = params.gsid;
    } else {
      this.d.hud.setStatus('Logging in…');
      const login = await this.d.api.login({
        userName: params.userName,
        agentId,
        password: DEFAULT_PASSWORD,
      });
      if (!login.sessionId) throw new Error('Login response missing SessionId');

      this.d.api.setCredentials({
        sessionToken: login.sessionId,
        gameSessionId: null,
        agentId: login.agentId || agentId,
        userName: params.userName,
      });

      this.d.hud.setStatus('Opening game…');
      const session = await this.d.api.openGame({
        userName: params.userName,
        agentId: login.agentId || agentId,
        gameKey: GAME_KEY_SINGLE,
      });

      const parsed = parseGameUrl(session.currentGameUrl, login.sessionId);
      bearerToken = ensureUrlEncoded(parsed.gameToken);
      gsid = parsed.gsid || session.sessionId;

      this.d.state.setBalance(session.balance);
      this.applyServerConfig(session.chips, session.minBet, session.maxBet);
    }

    this.d.api.setCredentials({
      sessionToken: bearerToken,
      gameSessionId: gsid,
      agentId,
      userName: params.userName,
    });
    this.gid = gsid;

    this.d.hud.setStatus('Fetching balance…');
    try {
      const balance = await this.d.api.checkBalance({ sessionId: gsid });
      this.d.state.setBalance(balance);
    } catch (e) {
      warn('[Orchestrator] checkBalance failed (continuing):', e);
    }
  }

  /**
   * Refresh balance + game config + initialGameData from /action/getinfo.
   * Returns the first start card for round 1 (defaults to 9 if missing —
   * matches the Flutter fallback in game_bloc.dart cycle-start logic).
   */
  private async loadConfigAndBalance(): Promise<number> {
    const sessionId = this.d.api.getCredentials().gameSessionId ?? '';
    try {
      const info = await this.d.api.getInfo({ sessionId });
      if (info.balance > 0) this.d.state.setBalance(info.balance);
      this.applyServerConfig(info.chips, info.minBet, info.maxBet);
      const first = parseNextFirstCard(info.initialGameData);
      return first ?? 9;
    } catch (e) {
      warn('[Orchestrator] getInfo failed (continuing with defaults):', e);
      if (this.d.state.config.chipValues.length === 0) {
        this.d.state.setConfig({
          minBet: DEFAULT_GAME_CONFIG.minBet,
          maxBet: DEFAULT_GAME_CONFIG.maxBet,
          chipValues: [...DEFAULT_GAME_CONFIG.chipValues],
        });
      }
      return 9;
    }
  }

  private applyServerConfig(chips: number[], minBet: number, maxBet: number): void {
    const current = this.d.state.config;
    const nextChips = chips.length > 0 ? chips : [...current.chipValues];
    const nextMin = minBet > 0 ? minBet : current.minBet;
    const nextMax = maxBet > 0 ? maxBet : current.maxBet;
    debugLog(
      '[Orchestrator] applyServerConfig chips=',
      nextChips,
      'min=',
      nextMin,
      'max=',
      nextMax,
    );
    this.d.state.setConfig({
      minBet: nextMin,
      maxBet: nextMax,
      chipValues: nextChips,
    });
  }

  // =================================================================
  // Rive event bindings
  // =================================================================

  private bindRiveEvents(): void {
    this.d.rive.on('placeBetClick', (payload) => {
      const id = (payload.betID ?? payload.betId ?? payload.id) as number | undefined;
      if (typeof id !== 'number') return;
      const bet = betTypeFromClickId(id);
      if (bet) void this.tryPlaceAndConfirm(bet);
    });

    this.d.rive.on('evtClickChips', (payload) => {
      const amt = (payload.chipsAmt ?? payload.chipAmount) as number | undefined;
      if (typeof amt === 'number') {
        const idx = this.d.state.config.chipValues.indexOf(amt);
        if (idx >= 0) this.d.state.selectChip(idx);
      } else {
        const i = (payload.chipIndex ?? payload.index) as number | undefined;
        if (typeof i === 'number') this.d.state.selectChip(i);
      }
    });

    this.d.rive.on('evtClearClick', () => this.clearAll());

    this.d.rive.on('UIButtonClick', (payload) => {
      const parts = String(payload.parts ?? '');
      if (parts === 'startBetEnd') {
        this.d.rive.triggerGirlAnim(1);
      } else if (parts === 'noMoreBet_End') {
        if (this.didStartReveal) return;
        this.didStartReveal = true;
        this.playResultSequence();
      }
    });
  }

  // =================================================================
  // Player actions
  // =================================================================

  /**
   * placeBetClick auto-confirms — no separate Play step. Mirrors
   * game_bloc.dart:548-604 placeBetClick branch.
   */
  private async tryPlaceAndConfirm(bet: BetType): Promise<void> {
    const s = this.d.state;
    if (s.phase !== 'betting') return;
    if (!s.bets.isEmpty) {
      // HighLow auto-confirms on the first click — block additional clicks
      // until the round resolves.
      return;
    }

    const chip = s.selectedChipValue;
    const chipIndex = s.selectedChipIndex;
    const ok = s.placeBet(bet, chip);
    if (!ok) {
      debugLog('[Orchestrator] placeBet rejected', bet, chip);
      return;
    }
    this.d.chips.placeChip(bet, chip, chipIndex);

    await this.confirm();
  }

  clearAll(): void {
    if (this.d.state.phase !== 'betting') return;
    this.d.state.clearBets();
    this.d.chips.hardClear();
  }

  /** Submit bets and drive the result flow. */
  async confirm(): Promise<void> {
    if (this.d.state.phase !== 'betting') return;

    const bets = this.d.state.bets.toBetLines();
    if (bets.length === 0) return;

    this.d.state.setPhase('bettingClosed');

    try {
      const sid = this.d.api.getCredentials().gameSessionId ?? '';
      const result = await this.d.api.placeBet({
        sessionId: sid,
        bets,
        gid: this.gid || sid,
      });

      this.gid = result.nextRoundId || this.gid;

      const round = this.d.state.currentRound;
      const totalBet = this.d.state.bets.total;
      const balanceBefore = this.d.state.balance;
      const betDetails = formatBetDetails(this.d.state.bets);

      this.d.state.applyRoundResult({
        round,
        firstCard: result.firstCard,
        secondCard: result.secondCard,
        result:
          result.secondCard === result.firstCard
            ? 'tie'
            : result.winningBetItemId === BET_ITEM_ID.high
            ? 'high'
            : result.winningBetItemId === BET_ITEM_ID.low
            ? 'low'
            : result.secondCard > result.firstCard
            ? 'high'
            : 'low',
        totalWinLose: result.totalWinLose,
        totalBet,
        positiveWinAmount: result.positiveWinAmount,
        hasPositiveWin: result.hasPositiveWin,
      });

      // Defer the balance update to the win/lose reveal (t=3000 in
      // playResultSequence) so the displayed balance stays "bet deducted"
      // through the card reveal and only reflects winnings when the result
      // is shown (req 2).
      this.pendingSettleBalance = result.afterBalance;

      // Capture the next-cycle's first card on round 3 ONLY.
      if (round === ROUNDS_PER_CYCLE) {
        this.d.state.setNextCycleFirstCard(result.nextFirstCard);
      }

      // Stash the result card; the Rive update happens at t=1000ms in
      // playResultSequence.
      this.pendingSecondCard = result.secondCard;
      this.pendingNextRoundStartCard = round < ROUNDS_PER_CYCLE ? result.nextFirstCard : null;

      GameLogManager.add({
        betAmount: totalBet,
        betDetails,
        winLoseAmount: result.totalWinLose,
        balanceBefore,
        balanceAfter: result.afterBalance,
        gameResult: formatGameResult(this.d.state.lastRoundResult?.result ?? 'tie'),
        roundId: this.gid,
      });

      this.d.state.setPhase('cardReveal');
      this.d.hud.setStatus('No more bets');

      // Safety net: if Rive's UIButtonClick noMoreBet_End never fires, kick
      // the result sequence after 2s.
      this.scheduleTimer(() => {
        if (this.didStartReveal) return;
        this.didStartReveal = true;
        this.playResultSequence();
      }, 2000);
    } catch (e) {
      error('[Orchestrator] placeBet failed:', e);
      const msg = extractMessage(e);
      if (e instanceof ApiError && e.unauthorized) {
        this.d.state.setError('Session expired. Please reload.');
      } else {
        this.d.state.setError(msg);
      }
      this.d.hud.setStatus('ERROR: ' + msg);
    }
  }

  // =================================================================
  // Cycle / round orchestration
  // =================================================================

  private pendingSecondCard: number | null = null;
  private pendingNextRoundStartCard: number | null = null;

  /**
   * Start a fresh 3-round cycle. Sets card slot 1 to the initial first card
   * and begins round 1's betting phase.
   */
  private startCycle(firstCard: number): void {
    this.cancelTimers();
    this.d.state.resetCards();
    this.d.state.setCurrentRound(1);
    this.d.state.setCardSlot(1, firstCard);
    this.d.rive.setCardValue(1, firstCard);
    this.d.rive.setRoundCounter(1);
    this.d.rive.setYellowPointer(true);
    this.startBettingRound();
  }

  /** Begin a betting round (1..3). */
  private startBettingRound(): void {
    // Safety net: ensure the previous round's balance was settled even if
    // the t=3000 win/lose timer was skipped (e.g. cancelled by an early
    // cycle reset).
    this.applySettlement();

    this.didStartReveal = false;
    this.didShowResult = false;
    this.isPlayResultRunning = false;
    this.pendingSettleBalance = null;
    this.didSettle = false;

    this.d.state.resetForNewRound();
    this.d.chips.hardClear();
    this.d.state.setPhase('betting');
    this.d.hud.setStatus(`Round ${this.d.state.currentRound} — place your bets`);
  }

  /**
   * Play-result sequence. Mirrors `_onPlayResultSequence`
   * (game_bloc.dart:352-396):
   *
   *   t = 1000 ms — reveal the result card; on round 3 also fire Card6Continues
   *   t = 3000 ms — settle the deferred balance, animate chips, show win/lose
   *   t = 7000 ms — if round < 3 → next round; else → cycleComplete
   */
  private playResultSequence(): void {
    if (this.isPlayResultRunning) return;
    this.isPlayResultRunning = true;
    this.cancelTimers();

    const round = this.d.state.currentRound;
    const resultSlot = (round * 2) as CardSlot; // 2, 4, or 6
    const secondCard = this.pendingSecondCard;
    if (secondCard != null) {
      // Note: state slot is set immediately too so onStateChange syncs.
      this.d.state.setCardSlot(resultSlot, secondCard);
    }

    // t = 1000 ms — reveal the result card
    this.scheduleTimer(() => {
      this.d.rive.setRoundCounter(resultSlot);
      if (secondCard != null) {
        this.d.rive.setCardValue(resultSlot, secondCard);
      }
      if (resultSlot === 6) {
        this.d.rive.setYellowPointer(false);
        this.d.rive.triggerPlayCard6();
      }
    }, 1000);

    // t = 3000 ms — settle balance + animate chips + show result text
    this.scheduleTimer(() => {
      if (this.didShowResult) return;
      this.didShowResult = true;

      const r = this.d.state.lastRoundResult;
      const winLose = r?.totalWinLose ?? 0;
      // Capture the winning-bet set BEFORE settling — settle() clears the
      // resolved bets, and placedWinningBets() reads them.
      const winningBets = this.placedWinningBets(r?.result ?? 'tie');

      // Reflect the post-round balance now that the result is shown (req 2).
      // Clears the resolved bets in the same commit so the available balance
      // lands exactly on the server's settled value.
      this.applySettlement();

      if (winLose > 0) {
        this.d.chips.animateWinningChips(winningBets);
        this.d.hud.setStatus(`YOU WIN ${formatCurrency(winLose)}`);
      } else if (winLose < 0) {
        this.d.chips.animateLosingChips();
        this.d.hud.setStatus(`YOU LOSE ${formatCurrency(-winLose)}`);
      } else {
        this.d.chips.animateTie();
        this.d.hud.setStatus('PUSH');
      }
      this.d.state.setPhase('showResult');
    }, 3000);

    // t = 7000 ms — next round or cycle-complete
    this.scheduleTimer(() => {
      this.d.chips.hardClear();
      const cur = this.d.state.currentRound;
      if (cur < ROUNDS_PER_CYCLE) {
        this.advanceToNextRound();
      } else {
        this.cycleComplete();
      }
    }, 7000);
  }

  /**
   * Move to the next betting round within the current cycle.
   * Mirrors `_onStartNewBettingRound` — sets the new first card at slot
   * (nextRound-1)*2 + 1 (i.e. slot 3 for round 2, slot 5 for round 3).
   */
  private advanceToNextRound(): void {
    const nextRound = this.d.state.currentRound + 1;
    if (nextRound > ROUNDS_PER_CYCLE) return;
    const startSlot = ((nextRound - 1) * 2 + 1) as CardSlot;
    const firstCard = this.pendingNextRoundStartCard ?? 9;

    this.d.state.setCurrentRound(nextRound);
    this.d.state.setCardSlot(startSlot, firstCard);
    this.d.rive.setCardValue(startSlot, firstCard);
    this.d.rive.setRoundCounter(startSlot);
    this.d.rive.setYellowPointer(true);

    this.pendingSecondCard = null;
    this.pendingNextRoundStartCard = null;

    this.startBettingRound();
  }

  /**
   * End-of-cycle shuffle. Mirrors `_onCycleComplete` (game_bloc.dart:486-536):
   *   t = 2000 ms — setRoundCounter(0), setYellowPointer(false)
   *   t = 3000 ms — triggerCardReset
   *   t = 4000 ms — triggerShuffle
   *   t = 9000 ms — setCardValue(1, nextCycleFirstCard); roundCounter=1; yellow=true
   *   t = 10000 ms — startBettingRound(round=1)
   */
  private cycleComplete(): void {
    this.cancelTimers();
    this.d.state.setPhase('shuffling');
    this.d.hud.setStatus('Shuffling…');

    this.scheduleTimer(() => {
      this.d.rive.setRoundCounter(0);
      this.d.rive.setYellowPointer(false);
      this.d.state.resetCards();
    }, 2000);

    this.scheduleTimer(() => {
      this.d.rive.triggerCardReset();
    }, 3000);

    this.scheduleTimer(() => {
      this.d.rive.triggerShuffle();
    }, 4000);

    this.scheduleTimer(() => {
      const first = this.d.state.nextCycleFirstCard ?? 9;
      this.d.state.setNextCycleFirstCard(null);
      this.d.state.setCurrentRound(1);
      this.d.state.setCardSlot(1, first);
      this.d.rive.setCardValue(1, first);
      this.d.rive.setRoundCounter(1);
      this.d.rive.setYellowPointer(true);
    }, 9000);

    this.scheduleTimer(() => {
      this.pendingSecondCard = null;
      this.pendingNextRoundStartCard = null;
      this.startBettingRound();
    }, 10000);
  }

  /**
   * Apply the deferred balance settlement: set the wallet to the server's
   * post-round value and clear the resolved bets (atomically, to avoid a
   * one-frame flash of an intermediate available balance). Idempotent — safe
   * to call from the t=3000 timer and again from the startBettingRound
   * safety net. No-op when nothing is pending (e.g. a fresh round).
   */
  private applySettlement(): void {
    if (this.didSettle) return;
    if (this.pendingSettleBalance == null) return;
    this.didSettle = true;
    this.d.state.settle(this.pendingSettleBalance);
  }

  // =================================================================
  // State-change → output sync
  // =================================================================

  private onStateChange(next: GameStateSnapshot, prev: GameStateSnapshot): void {
    if (next.balance !== prev.balance) {
      this.renderBalance();
    }

    if (next.bets !== prev.bets) {
      this.d.hud.setBetTotal(next.bets.total);
      // Placing or clearing a bet changes the available balance — reflect
      // it immediately (req: balance updates the instant a bet is placed).
      this.renderBalance();
      this.d.controls.setControlsEnabled(
        next.phase === 'betting',
        !next.bets.isEmpty,
      );
    }

    if (next.selectedChipIndex !== prev.selectedChipIndex) {
      this.d.controls.setSelectedChip(next.selectedChipIndex);
    }

    if (next.phase !== prev.phase) {
      this.d.controls.setControlsEnabled(
        next.phase === 'betting',
        !next.bets.isEmpty,
      );
    }

    if (next.config !== prev.config) {
      this.d.controls.bindChips(next.config);
      this.d.controls.setSelectedChip(next.selectedChipIndex);
    }
  }

  /** Push the available balance (wallet − pending bets) to the HUD. */
  private renderBalance(): void {
    this.d.hud.setBalance(this.d.state.availableBalance);
  }

  // =================================================================
  // Helpers
  // =================================================================

  private placedWinningBets(winner: 'high' | 'low' | 'tie'): Set<BetType> {
    const out = new Set<BetType>();
    if (winner === 'tie') return out; // push — no winners
    for (const b of this.d.state.bets.bets) {
      if (b === winner) out.add(b);
    }
    return out;
  }

  private scheduleTimer(fn: () => void, ms: number): void {
    const id = window.setTimeout(fn, ms);
    this.sequenceTimers.push(id);
  }

  private cancelTimers(): void {
    for (const id of this.sequenceTimers) window.clearTimeout(id);
    this.sequenceTimers = [];
  }
}

// =====================================================================
// Pure helpers
// =====================================================================

function formatCurrency(n: number): string {
  return Math.round(n).toLocaleString('en-US');
}

function ensureUrlEncoded(token: string): string {
  if (!token) return token;
  if (token.includes('%')) return token;
  return encodeURIComponent(token);
}

function parseGameUrl(
  currentGameUrl: string,
  loginToken: string,
): { gameToken: string; gsid: string } {
  if (!currentGameUrl) return { gameToken: loginToken, gsid: '' };
  try {
    const u = new URL(currentGameUrl, 'https://placeholder.invalid');
    const urlToken = u.searchParams.get('token');
    const urlGsid = u.searchParams.get('gsid');
    return {
      gameToken: urlToken && urlToken.length > 0 ? urlToken : loginToken,
      gsid: urlGsid ?? '',
    };
  } catch {
    return { gameToken: loginToken, gsid: '' };
  }
}

function formatGameResult(winner: 'high' | 'low' | 'tie'): string {
  if (winner === 'tie') return 'Tie';
  return winner === 'high' ? 'High Win' : 'Low Win';
}

function formatBetDetails(bets: {
  bets: readonly BetType[];
  amountFor(b: BetType): number;
}): string {
  const parts: string[] = [];
  const label: Record<BetType, string> = { high: 'High', low: 'Low' };
  for (const b of bets.bets) {
    const amt = bets.amountFor(b);
    if (amt > 0) parts.push(`${label[b]}:${formatCurrency(amt)}`);
  }
  return parts.join(', ');
}

function extractMessage(e: unknown): string {
  if (e instanceof Error) return e.message;
  if (typeof e === 'string') return e;
  try {
    return JSON.stringify(e);
  } catch {
    return String(e);
  }
}
